import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { CheckCircle2, Circle, ArrowRight } from "lucide-react";
import type { Applicant, ApplicationStage } from "@/types/applicant";
import { stageLabels, stageColors, stageOrder } from "@/types/applicant";

interface ApplicantStageManagerProps {
  applicant: Applicant;
  onStageChange: (stage: ApplicationStage, notes?: string) => void;
}

const ApplicantStageManager = ({ applicant, onStageChange }: ApplicantStageManagerProps) => {
  const [selectedStage, setSelectedStage] = useState<ApplicationStage | null>(null);
  const [notes, setNotes] = useState("");

  const currentStageIndex = stageOrder.indexOf(applicant.current_stage);
  const displayStages = stageOrder.filter(s => s !== "rejected");

  const handleStageClick = (stage: ApplicationStage) => {
    if (stage === applicant.current_stage) return;
    setSelectedStage(stage);
  };

  const confirmStageChange = () => {
    if (selectedStage) {
      onStageChange(selectedStage, notes || undefined);
      setSelectedStage(null);
      setNotes("");
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Bewerbungsprozess</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-6">
          {/* Stage Timeline */}
          <div className="relative">
            <div className="flex items-center justify-between">
              {displayStages.map((stage, index) => {
                const stageIndex = stageOrder.indexOf(stage);
                const isCompleted = stageIndex < currentStageIndex;
                const isCurrent = stage === applicant.current_stage;
                const isSelected = stage === selectedStage;

                return (
                  <div key={stage} className="flex flex-col items-center relative z-10">
                    <button
                      onClick={() => handleStageClick(stage)}
                      disabled={isCurrent}
                      className={`
                        w-10 h-10 rounded-full flex items-center justify-center transition-all
                        ${isCompleted ? stageColors[stage] + " text-white" : ""}
                        ${isCurrent ? stageColors[stage] + " text-white ring-4 ring-offset-2 ring-primary/30" : ""}
                        ${!isCompleted && !isCurrent ? "bg-muted hover:bg-muted/80" : ""}
                        ${isSelected ? "ring-4 ring-offset-2 ring-primary" : ""}
                        ${isCurrent ? "cursor-default" : "cursor-pointer"}
                      `}
                    >
                      {isCompleted ? (
                        <CheckCircle2 className="h-5 w-5" />
                      ) : (
                        <Circle className="h-5 w-5" />
                      )}
                    </button>
                    <span className={`
                      mt-2 text-xs text-center max-w-16
                      ${isCurrent ? "font-semibold text-foreground" : "text-muted-foreground"}
                    `}>
                      {stageLabels[stage]}
                    </span>
                    {index < displayStages.length - 1 && (
                      <ArrowRight className="absolute left-full top-5 -translate-y-1/2 h-4 w-4 text-muted-foreground hidden sm:block" style={{ marginLeft: "-0.5rem" }} />
                    )}
                  </div>
                );
              })}
            </div>
            {/* Progress Line */}
            <div className="absolute top-5 left-0 right-0 h-0.5 bg-muted -z-0">
              <div
                className="h-full bg-primary transition-all"
                style={{
                  width: `${(currentStageIndex / (displayStages.length - 1)) * 100}%`,
                }}
              />
            </div>
          </div>

          {/* Stage Change Confirmation */}
          {selectedStage && (
            <Card className="bg-muted/50">
              <CardContent className="pt-4 space-y-4">
                <p className="text-sm">
                  Phase ändern von{" "}
                  <span className="font-semibold">{stageLabels[applicant.current_stage]}</span>{" "}
                  zu{" "}
                  <span className="font-semibold">{stageLabels[selectedStage]}</span>?
                </p>
                <div className="space-y-2">
                  <Label>Notiz (optional)</Label>
                  <Textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Grund für die Änderung..."
                    rows={2}
                  />
                </div>
                <div className="flex gap-2">
                  <Button onClick={confirmStageChange}>Bestätigen</Button>
                  <Button
                    variant="outline"
                    onClick={() => {
                      setSelectedStage(null);
                      setNotes("");
                    }}
                  >
                    Abbrechen
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Stage Info */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {displayStages.map((stage) => {
              const stageIndex = stageOrder.indexOf(stage);
              const isCompleted = stageIndex < currentStageIndex;
              const isCurrent = stage === applicant.current_stage;

              return (
                <div
                  key={stage}
                  className={`
                    p-3 rounded-lg border
                    ${isCurrent ? "border-primary bg-primary/5" : "border-border"}
                  `}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <div className={`w-3 h-3 rounded-full ${stageColors[stage]}`} />
                    <span className="font-medium text-sm">{stageLabels[stage]}</span>
                    {isCompleted && (
                      <CheckCircle2 className="h-4 w-4 text-green-500 ml-auto" />
                    )}
                    {isCurrent && (
                      <span className="text-xs bg-primary text-primary-foreground px-2 py-0.5 rounded ml-auto">
                        Aktuell
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {getStageDescription(stage)}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const getStageDescription = (stage: ApplicationStage): string => {
  const descriptions: Record<ApplicationStage, string> = {
    new: "Bewerbung eingegangen, noch kein Kontakt aufgenommen",
    first_contact: "Erstkontakt hergestellt, Informationen ausgetauscht",
    phone_interview: "Telefonisches Kennenlernen durchgeführt",
    onsite_interview: "Persönliches Vorstellungsgespräch vor Ort",
    trial_work: "Probearbeiten zur praktischen Eignungsprüfung",
    offer: "Angebot unterbreitet, Vertrag in Vorbereitung",
    hired: "Eingestellt und in Mitarbeiter umgewandelt",
    rejected: "Absage erteilt",
  };
  return descriptions[stage];
};

export default ApplicantStageManager;
